import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { Toaster } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { installDomTranslateGuard, isPageBeingTranslated } from "../lib/dom-translate-guard";
import { addBreadcrumb, installErrorLog, recordError } from "../lib/error-log";
import { isStaffLogoutReturnPending } from "@/lib/staff-access-link";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function isDomMutationError(error: unknown) {
  const msg = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  return (
    msg.includes("removeChild") ||
    msg.includes("insertBefore") ||
    msg.includes("The node to be removed is not a child of this node")
  );
}

function buildErrorReport(error: Error) {
  const componentStack = (error as unknown as { componentStack?: string })?.componentStack;
  const cause = (error as unknown as { cause?: unknown })?.cause;
  const lines = [
    `Rota: ${typeof window !== "undefined" ? window.location.pathname + window.location.search : ""}`,
    `Erro: ${error?.name}: ${error?.message}`,
    cause ? `Causa original: ${cause instanceof Error ? `${cause.name}: ${cause.message}` : String(cause)}` : "",
    typeof navigator !== "undefined" ? `Navegador: ${navigator.userAgent}` : "",
    typeof navigator !== "undefined" ? `Idioma: ${navigator.language}` : "",
    "",
    "--- Componentes React ---",
    componentStack?.trim() || "(não disponível)",
    "",
    "--- Stack ---",
    error?.stack?.trim() || "(não disponível)",
    cause instanceof Error && cause.stack ? `\n--- Stack da causa ---\n${cause.stack.trim()}` : "",
  ];
  return lines.filter(Boolean).join("\n");
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  const domMutation = isDomMutationError(error);
  const report = buildErrorReport(error);

  useEffect(() => {
    // Full diagnostics in the console so the exact component/stack is visible.
    console.error("[FutBux] Route error", {
      name: error?.name,
      message: error?.message,
      route: typeof window !== "undefined" ? window.location.pathname : "",
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      language: typeof navigator !== "undefined" ? navigator.language : "",
      translated:
        typeof document !== "undefined" &&
        (document.documentElement.classList.contains("translated-ltr") ||
          document.documentElement.classList.contains("translated-rtl") ||
          !!document.querySelector("font[_msttexthash], font[class*='notranslate']")),
      cause: (error as unknown as { cause?: unknown })?.cause,
      stack: error?.stack,
      componentStack: (error as unknown as { componentStack?: string })?.componentStack,
    });
    recordError({
      kind: "boundary",
      error,
      componentStack: (error as unknown as { componentStack?: string })?.componentStack,
    });
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);




  return (
    <div className="flex min-h-screen items-start justify-center bg-background px-4 py-10" translate="no">
      <div className="w-full max-w-2xl text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          Esta página não carregou
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {domMutation
            ? "A tradução automática do navegador (ou uma extensão que altera a página) entrou em conflito com a aplicação. Desative a tradução automática para este site e tente novamente."
            : "Algo deu errado. Tente novamente ou volte para o início."}
        </p>
        <p className="mt-2 break-words text-xs text-muted-foreground/80">
          {error?.name}: {error?.message}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Tentar novamente
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Ir para o início
          </a>
          <a
            href="/diagnostico"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Ver diagnóstico
          </a>
          <button
            onClick={() => {
              navigator.clipboard?.writeText(report).catch(() => undefined);
            }}
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Copiar relatório
          </button>
        </div>

        <details className="mt-6 text-left" open>
          <summary className="cursor-pointer text-sm font-medium text-foreground">
            Detalhes técnicos (componente, stack e causa original)
          </summary>
          <textarea
            readOnly
            value={report}
            className="mt-2 h-72 w-full rounded border bg-muted p-2 font-mono text-[11px]"
          />
        </details>


      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "google", content: "notranslate" },
      { title: "FutBux — Gestão de Centros de Treinamento" },
      { name: "description", content: "FutGestao is a training center management application." },
      { name: "author", content: "Lovable" },
      { property: "og:title", content: "FutBux — Gestão de Centros de Treinamento" },
      { property: "og:description", content: "FutGestao is a training center management application." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:site", content: "@Lovable" },
      { name: "twitter:title", content: "FutBux — Gestão de Centros de Treinamento" },
      { name: "twitter:description", content: "FutGestao is a training center management application." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/08ac1b5b-9515-403b-aeb5-2f03dd8dbfd0/id-preview-be771e7b--deffebd9-9fd1-4783-be35-669fadd07128.lovable.app-1782735888740.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/08ac1b5b-9515-403b-aeb5-2f03dd8dbfd0/id-preview-be771e7b--deffebd9-9fd1-4783-be35-669fadd07128.lovable.app-1782735888740.png" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="pt-BR" translate="no" className="notranslate">
      <head>
        <HeadContent />
      </head>
      <body className="notranslate" translate="no">
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    installErrorLog();
    installDomTranslateGuard();
    if (isPageBeingTranslated()) {
      console.warn("[FutBux] Página está sendo traduzida pelo navegador — pode causar erros de DOM.");
    }
  }, []);

  useEffect(() => {
    return router.subscribe("onResolved", ({ toLocation }) => {
      addBreadcrumb(`navegou para ${toLocation.pathname}`);
    });
  }, [router]);

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((event) => {
      addBreadcrumb(`auth: ${event}`);
      // O logout por PIN já fará uma navegação completa para o link público.
      // Invalidar a rota protegida aqui concorria com esse redirecionamento.
      if (event === "SIGNED_OUT" && isStaffLogoutReturnPending()) return;
      if (event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "USER_UPDATED") {
        router.invalidate();
      }
    });
    return () => {
      data.subscription.unsubscribe();
    };
  }, [router]);


  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
      <Toaster position="top-right" />
    </QueryClientProvider>
  );
}
